import { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries, HistogramSeries } from "lightweight-charts";
import { subscribe, fetchCandles } from "../../lib/hyperliquid";
import { formatPrice } from "../../lib/format";

const INTERVALS = ["1m", "5m", "15m", "1h"];
const LOOKBACK_MS = {
    "1m": 1000 * 60 * 60 * 4,
    "5m": 1000 * 60 * 60 * 24,
    "15m": 1000 * 60 * 60 * 72,
    "1h": 1000 * 60 * 60 * 24 * 14,
};

// Reject candles with non-positive prices (defensive — bad ws frames have
// crashed the price scale and crushed the candles into the corner before).
const isValidCandle = (c) =>
    c && Number.isFinite(+c.o) && +c.o > 0 &&
    Number.isFinite(+c.h) && +c.h > 0 &&
    Number.isFinite(+c.l) && +c.l > 0 &&
    Number.isFinite(+c.c) && +c.c > 0;

export default function PriceChart({ coin, quote = "USD" }) {
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const candleRef = useRef(null);
    const volumeRef = useRef(null);
    const [interval, setInterval] = useState("1m");
    const [stats, setStats] = useState({ last: null, high: null, low: null, change: null });
    const isCross = quote && quote !== "USD";
    const pairLabel = isCross ? `${coin}-${quote}` : `${coin}-USD`;

    /* ----- chart lifecycle ----- */
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const chart = createChart(el, {
            width: el.clientWidth,
            height: el.clientHeight,
            layout: {
                background: { type: "solid", color: "transparent" },
                textColor: "#a1a1aa",
                fontFamily: "IBM Plex Mono, monospace",
                fontSize: 11,
                attributionLogo: false,
            },
            localization: { locale: "en-US" },
            grid: {
                vertLines: { color: "rgba(255,255,255,0.04)" },
                horzLines: { color: "rgba(255,255,255,0.04)" },
            },
            timeScale: {
                borderColor: "#1F1F24",
                timeVisible: true,
                secondsVisible: false,
                rightOffset: 4,
                barSpacing: 6,
            },
            rightPriceScale: {
                borderColor: "#1F1F24",
                scaleMargins: { top: 0.08, bottom: 0.24 }, // reserve 24% at bottom for the volume overlay
                autoScale: true,
            },
            crosshair: {
                mode: 1,
                vertLine: { color: "rgba(34,211,238,0.5)", labelBackgroundColor: "#06b6d4" },
                horzLine: { color: "rgba(34,211,238,0.5)", labelBackgroundColor: "#06b6d4" },
            },
        });

        const candles = chart.addSeries(CandlestickSeries, {
            upColor: "#10b981",
            downColor: "#f43f5e",
            borderUpColor: "#10b981",
            borderDownColor: "#f43f5e",
            wickUpColor: "#10b981",
            wickDownColor: "#f43f5e",
            priceLineColor: "rgba(34,211,238,0.7)",
            priceLineWidth: 1,
            priceLineStyle: 2, // dashed
        });

        const volume = chart.addSeries(HistogramSeries, {
            priceFormat: { type: "volume" },
            priceScaleId: "vol",
            color: "rgba(34,211,238,0.35)",
        });
        chart.priceScale("vol").applyOptions({
            scaleMargins: { top: 0.78, bottom: 0.02 },
        });

        chartRef.current = chart;
        candleRef.current = candles;
        volumeRef.current = volume;

        // ResizeObserver: lightweight-charts' autoSize occasionally misses
        // late layout reflows (e.g. after the sidebar/order-book renders).
        // This guarantees we always match the container's true bounding box.
        const ro = new ResizeObserver(() => {
            if (!chartRef.current || !el) return;
            chartRef.current.applyOptions({ width: el.clientWidth, height: el.clientHeight });
        });
        ro.observe(el);

        return () => {
            ro.disconnect();
            try { chart.remove(); } catch {}
            chartRef.current = null;
            candleRef.current = null;
            volumeRef.current = null;
        };
    }, []);

    /* ----- data fetch + live subscription ----- */
    useEffect(() => {
        if (!coin || !chartRef.current) return;
        let mounted = true;
        let unsubBase = null;
        let unsubQuote = null;
        // Latest candles per timestamp for each leg — used to synthesise the
        // cross-pair ratio live (Hyperliquid has no native cross-pair feed).
        const baseMap = new Map();
        const quoteMap = new Map();

        candleRef.current?.setData([]);
        volumeRef.current?.setData([]);

        const applyCross = (isInitial = false) => {
            if (!candleRef.current) return;
            const cs = [];
            const vs = [];
            const commonTs = [...baseMap.keys()].filter((t) => quoteMap.has(t)).sort((a, b) => a - b);
            for (const t of commonTs) {
                const b = baseMap.get(t);
                const q = quoteMap.get(t);
                if (!q.o || !q.h || !q.l || !q.c) continue;
                const open = b.o / q.o;
                const high = b.h / q.l;
                const low = b.l / q.h;
                const close = b.c / q.c;
                cs.push({ time: t, open, high, low, close });
                vs.push({
                    time: t,
                    value: Math.max(0, +b.v || 0),
                    color: close >= open ? "rgba(16,185,129,0.45)" : "rgba(244,63,94,0.45)",
                });
            }
            if (!cs.length) return;
            // On the initial backfill, replace the series and refit. On every
            // subsequent live tick, just update the latest bar so we never
            // steal the user's zoom / pan.
            if (isInitial) {
                candleRef.current.setData(cs);
                volumeRef.current.setData(vs);
                chartRef.current.timeScale().fitContent();
                chartRef.current.priceScale("right").applyOptions({ autoScale: true });
            } else {
                const lastC = cs[cs.length - 1];
                const lastV = vs[vs.length - 1];
                candleRef.current.update(lastC);
                volumeRef.current.update(lastV);
            }
            const last = cs[cs.length - 1];
            const first = cs[0];
            const high = Math.max(...cs.map((c) => c.high));
            const low = Math.min(...cs.map((c) => c.low));
            const change = ((last.close - first.open) / first.open) * 100;
            setStats({ last: last.close, high, low, change });
        };

        const applySingle = (raw) => {
            const clean = raw.filter(isValidCandle);
            if (!clean.length) return;
            const cs = clean.map((c) => ({
                time: Math.floor(c.t / 1000),
                open: +c.o, high: +c.h, low: +c.l, close: +c.c,
            }));
            const vs = clean.map((c) => ({
                time: Math.floor(c.t / 1000),
                value: Math.max(0, +c.v || 0),
                color: +c.c >= +c.o ? "rgba(16,185,129,0.45)" : "rgba(244,63,94,0.45)",
            }));
            candleRef.current.setData(cs);
            volumeRef.current.setData(vs);
            const last = cs[cs.length - 1];
            const first = cs[0];
            const high = Math.max(...cs.map((c) => c.high));
            const low = Math.min(...cs.map((c) => c.low));
            const change = ((last.close - first.open) / first.open) * 100;
            setStats({ last: last.close, high, low, change });
            chartRef.current.timeScale().fitContent();
            chartRef.current.priceScale("right").applyOptions({ autoScale: true });
        };

        (async () => {
            try {
                if (!isCross) {
                    const raw = await fetchCandles(coin, interval, LOOKBACK_MS[interval] || 1000 * 60 * 60 * 4);
                    if (!mounted || !Array.isArray(raw)) return;
                    applySingle(raw);
                } else {
                    // Fetch BOTH legs and synthesise the ratio. Hyperliquid is
                    // USD-only — there is no native cross-pair candle.
                    const [baseRaw, quoteRaw] = await Promise.all([
                        fetchCandles(coin, interval, LOOKBACK_MS[interval] || 1000 * 60 * 60 * 4),
                        fetchCandles(quote, interval, LOOKBACK_MS[interval] || 1000 * 60 * 60 * 4),
                    ]);
                    if (!mounted) return;
                    const cleanB = (Array.isArray(baseRaw) ? baseRaw : []).filter(isValidCandle);
                    const cleanQ = (Array.isArray(quoteRaw) ? quoteRaw : []).filter(isValidCandle);
                    cleanB.forEach((c) => baseMap.set(Math.floor(c.t / 1000), { o: +c.o, h: +c.h, l: +c.l, c: +c.c, v: +c.v || 0 }));
                    cleanQ.forEach((c) => quoteMap.set(Math.floor(c.t / 1000), { o: +c.o, h: +c.h, l: +c.l, c: +c.c }));
                    applyCross(true);
                }
            } catch {}

            if (!isCross) {
                unsubBase = subscribe({ type: "candle", coin, interval }, (c) => {
                    if (!candleRef.current || !isValidCandle(c)) return;
                    candleRef.current.update({
                        time: Math.floor(c.t / 1000),
                        open: +c.o, high: +c.h, low: +c.l, close: +c.c,
                    });
                    volumeRef.current.update({
                        time: Math.floor(c.t / 1000),
                        value: Math.max(0, +c.v || 0),
                        color: +c.c >= +c.o ? "rgba(16,185,129,0.45)" : "rgba(244,63,94,0.45)",
                    });
                    setStats((s) => ({
                        last: +c.c,
                        high: s.high == null ? +c.h : Math.max(s.high, +c.h),
                        low: s.low == null ? +c.l : Math.min(s.low, +c.l),
                        change: s.change,
                    }));
                });
            } else {
                // Subscribe to both legs; recompute the ratio bar on every tick.
                unsubBase = subscribe({ type: "candle", coin, interval }, (c) => {
                    if (!isValidCandle(c)) return;
                    baseMap.set(Math.floor(c.t / 1000), { o: +c.o, h: +c.h, l: +c.l, c: +c.c, v: +c.v || 0 });
                    applyCross();
                });
                unsubQuote = subscribe({ type: "candle", coin: quote, interval }, (c) => {
                    if (!isValidCandle(c)) return;
                    quoteMap.set(Math.floor(c.t / 1000), { o: +c.o, h: +c.h, l: +c.l, c: +c.c });
                    applyCross();
                });
            }
        })();

        return () => {
            mounted = false;
            if (unsubBase) unsubBase();
            if (unsubQuote) unsubQuote();
        };
    }, [coin, quote, interval, isCross]);

    const changeAcc = stats.change == null ? "text-zinc-400" : stats.change >= 0 ? "text-emerald-400" : "text-rose-400";

    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm flex flex-col h-full" data-testid="price-chart-panel">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[#1F1F24] flex-wrap">
                <div className="flex items-center gap-4">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Price Action</div>
                        <div className="flex items-center gap-2 mt-0.5">
                            <div className="text-sm font-medium font-mono tracking-tight">{pairLabel}</div>
                            {isCross && (
                                <span className="text-[8.5px] font-mono uppercase tracking-[0.18em] text-violet-300 border border-violet-500/40 bg-violet-500/10 px-1.5 py-0.5 rounded-sm">
                                    synthetic · {coin}/USD ÷ {quote}/USD
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-4 font-mono text-[10.5px]">
                        <Stat label="Last" value={stats.last != null ? formatPrice(stats.last) : "—"} accent="text-cyan-400" />
                        <Stat label="24h Δ" value={stats.change != null ? `${stats.change >= 0 ? "+" : ""}${stats.change.toFixed(2)}%` : "—"} accent={changeAcc} />
                        <Stat label="24h High" value={stats.high != null ? formatPrice(stats.high) : "—"} />
                        <Stat label="24h Low" value={stats.low != null ? formatPrice(stats.low) : "—"} />
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {INTERVALS.map((iv) => (
                        <button
                            key={iv}
                            data-testid={`chart-interval-${iv}`}
                            onClick={() => setInterval(iv)}
                            className={`text-[11px] font-mono px-2.5 py-1 rounded-sm border transition-colors ${
                                interval === iv
                                    ? "bg-cyan-400 text-black border-cyan-400"
                                    : "border-[#1F1F24] text-zinc-400 hover:text-zinc-50 hover:border-zinc-600"
                            }`}
                        >
                            {iv}
                        </button>
                    ))}
                </div>
            </div>

            {/* Chart canvas — fixed-height parent so flex children always have real estate */}
            <div ref={containerRef} className="flex-1 w-full min-h-[480px]" data-testid="price-chart-canvas" />
        </div>
    );
}

const Stat = ({ label, value, accent = "text-zinc-100" }) => (
    <div className="flex items-baseline gap-1.5">
        <span className="text-[9px] uppercase tracking-[0.18em] text-zinc-500">{label}</span>
        <span className={`tabular-nums ${accent}`}>{value}</span>
    </div>
);
